import React, { useRef, useEffect, useMemo } from "react";
import { Clock, Sun, Sunrise, Sunset, Moon } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { formatSlotLabel } from "../lib/dateLocal";
import { isAfterHoursTime } from "./LateReturnWarning";

const GROUPS = [
  { key: "morning",   label: "Morning",   icon: Sunrise, startH: 6,  endH: 12 },
  { key: "afternoon", label: "Afternoon", icon: Sun,     startH: 12, endH: 17 },
  { key: "evening",   label: "Evening",   icon: Sunset,  startH: 17, endH: 22 },
  { key: "night",     label: "Night",     icon: Moon,    startH: 22, endH: 6  },
];

function slotInGroup(slot, group) {
  const h = Number(slot.split(":")[0]);
  if (group.key === "night") return h >= group.startH || h < group.endH;
  return h >= group.startH && h < group.endH;
}

/**
 * Time slot picker with 12h labels grouped by time of day.
 *
 * Props:
 *   value / onChange / slots / disabled / testid — as before
 *   markAfterHours — cue after-hours slots (fee applies; still selectable)
 *   operatingOpen / operatingClose — listing hours for after-hours detection
 */
export default function TimeSlotPicker({
  value,
  onChange,
  slots = [],
  disabled = false,
  markAfterHours = false,
  operatingOpen,
  operatingClose,
  testid,
}) {
  const [open, setOpen] = React.useState(false);
  const selectedRef = useRef(null);

  useEffect(() => {
    if (open && selectedRef.current) {
      selectedRef.current.scrollIntoView({ block: "nearest", behavior: "instant" });
    }
  }, [open]);

  const grouped = useMemo(() => {
    return GROUPS.map((g) => ({
      ...g,
      slots: slots.filter((s) => slotInGroup(s, g)),
    })).filter((g) => g.slots.length > 0);
  }, [slots]);

  const displayLabel = value ? formatSlotLabel(value) : "Select time";
  const hasAfterHoursCue = markAfterHours && slots.some(
    (s) => isAfterHoursTime(s, operatingOpen, operatingClose),
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className="zc-input h-11 px-3 text-sm text-left flex items-center justify-between w-full min-w-0 gap-2"
          data-testid={testid}
        >
          <span className={value ? "" : "text-[var(--zc-text-3)]"}>
            {displayLabel}
          </span>
          <Clock size={14} className="text-[var(--zc-text-3)] shrink-0" />
        </button>
      </PopoverTrigger>

      <PopoverContent
        className="w-[260px] p-0 bg-white border border-[var(--zc-border)] z-[100] max-h-[320px] overflow-y-auto"
        align="start"
        sideOffset={6}
      >
        {hasAfterHoursCue && (
          <div
            className="zc-timeslot-fee-banner"
            data-testid={testid ? `${testid}-afterhours-banner` : undefined}
          >
            Night / outside host hours stay selectable — after-hours fee applies
          </div>
        )}
        {grouped.map((g) => {
          const Icon = g.icon;
          const groupHasFee = markAfterHours && g.slots.some(
            (s) => isAfterHoursTime(s, operatingOpen, operatingClose),
          );
          return (
            <div key={g.key}>
              <div className="zc-timeslot-group-header">
                <Icon size={13} />
                <span>{g.label}</span>
                {groupHasFee && (
                  <span className="zc-timeslot-group-header-fee" data-testid={testid ? `${testid}-night-fee-hint` : undefined}>
                    After-hours fee
                  </span>
                )}
              </div>
              <div className="grid grid-cols-3 gap-1 px-2 pb-2">
                {g.slots.map((s) => {
                  const selected = s === value;
                  const afterHrs = markAfterHours && isAfterHoursTime(s, operatingOpen, operatingClose);
                  return (
                    <button
                      key={s}
                      ref={selected ? selectedRef : undefined}
                      type="button"
                      onClick={() => { onChange?.(s); setOpen(false); }}
                      title={afterHrs ? "After-hours — fee applies" : undefined}
                      className={
                        "zc-timeslot-btn" +
                        (selected ? " zc-timeslot-active" : "") +
                        (afterHrs ? " zc-timeslot-has-fee" : "")
                      }
                      aria-pressed={selected}
                      aria-label={afterHrs ? `${formatSlotLabel(s)}, after-hours fee applies` : undefined}
                      data-testid={testid ? `${testid}-slot-${s}` : undefined}
                    >
                      <span className="zc-timeslot-btn-label">{formatSlotLabel(s)}</span>
                      {afterHrs && <span className="zc-timeslot-fee-tag">Fee</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
        {slots.length === 0 && (
          <div className="px-3 py-4 text-sm text-[var(--zc-text-3)] text-center">
            No time slots available
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
